// Standalone Auction draft creation with staged multi-image selection.
// Uses the shared resumable upload hook and authorized image metadata callable.
// Draft creation precedes uploads so files use the canonical item-specific path.
// Partial upload failures preserve the draft and let its normal gallery recover.
import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { ref, deleteObject } from "firebase/storage";
import { functions, storage } from "../firebase";
import { useUploadWithProgress } from "../hooks/use_upload_with_progress";

const emptyItem = { title: "", auctionSlug: "", description: "", startingBid: "25", minimumIncrement: "5", auctionNotes: "" };

export default function AuctionStandaloneForm({ eventId, onCreated, onCancel, onBusy, notify }) {
  const [form, setForm] = useState(emptyItem);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const { uploadFileWithProgress, progress } = useUploadWithProgress();
  const previews = useMemo(() => files.map((file) => ({ file, url: URL.createObjectURL(file) })), [files]);
  useEffect(() => () => previews.forEach(({ url }) => URL.revokeObjectURL(url)), [previews]);

  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (selected.some((file) => !/^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.size >= 5 * 1024 * 1024)) {
      notify("error", "Choose JPG, PNG, WebP, or GIF images smaller than 5 MB each.");
      return;
    }
    setFiles((previous) => [...previous, ...selected]);
  };
  const create = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    onBusy(true);
    let created = false;
    try {
      const result = await httpsCallable(functions, "createAuctionItem")({
        eventId, sourceType: "standalone", title: form.title.trim(), description: form.description,
        startingBidCents: Math.round(Number(form.startingBid) * 100),
        minimumIncrementCents: Math.round(Number(form.minimumIncrement) * 100),
        quantityAvailable: 1, auctionNotes: form.auctionNotes,
      });
      const itemId = result.data.auctionItemId;
      await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "auction", entityId: itemId, slug: form.auctionSlug || undefined });
      created = true;
      for (let index = 0; index < files.length; index += 1) {
        setUploadingIndex(index);
        const extension = files[index].type.split("/")[1].replace("jpeg", "jpg");
        const objectRef = ref(storage, `items/${eventId}/auction-${itemId}-${crypto.randomUUID()}.${extension}`);
        try {
          const imageUrl = await uploadFileWithProgress(objectRef, files[index]);
          await httpsCallable(functions, "manageAuctionItem")({ eventId, auctionItemId: itemId, action: "add_image", imageUrl });
        } catch (error) {
          await deleteObject(objectRef).catch(() => {});
          throw error;
        }
      }
      notify("success", "Standalone auction item created.");
    } catch (error) {
      notify("error", created
        ? "Auction draft created, but an image upload failed. Finish uploading images on its item card."
        : error.message || "Unable to create auction item.");
    } finally {
      setSaving(false);
      setUploadingIndex(null);
      onBusy(false);
      if (created) await onCreated();
    }
  };
  const totalProgress = uploadingIndex == null ? 0 : Math.round(((uploadingIndex + progress / 100) / files.length) * 100);

  return <section aria-labelledby="standalone-auction-heading">
    <h3 id="standalone-auction-heading" className="h5">Add Standalone Auction Item</h3>
    <form onSubmit={create}>
      <fieldset disabled={saving}>
        <div className="row g-3">
          {[["title", "Item Title", "text"], ["startingBid", "Starting Bid", "number"], ["minimumIncrement", "Minimum Increment", "number"]].map(([key, label, type]) => <div key={key} className="col-md-4">
            <label htmlFor={`new-${key}`} className="form-label">{label}</label>
            <input id={`new-${key}`} className="form-control" type={type} required min={key === "startingBid" ? 0 : 0.01} step={type === "number" ? "0.01" : undefined} value={form[key]} onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))} />
          </div>)}
          <div className="col-12 small text-muted">Each auction item represents one item. Create a separate auction item for each additional unit.</div>
          {[["description", "Description"], ["auctionNotes", "Auction Notes"]].map(([key, label]) => <div className="col-12" key={key}>
            <label className="form-label" htmlFor={`new-${key}`}>{label}</label>
            <textarea id={`new-${key}`} className="form-control" rows={3} value={form[key]} onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))} />
          </div>)}
          <div className="col-12"><label className="form-label" htmlFor="new-auction-slug">Public URL slug</label><input id="new-auction-slug" className="form-control" value={form.auctionSlug} onChange={(event) => setForm((previous) => ({ ...previous, auctionSlug: event.target.value }))} placeholder="auction-item" /><div className="form-text">Lowercase letters, numbers, and single hyphens. Leave empty for title-derived URL.</div></div>
          <div className="col-12">
            <label className="form-label" htmlFor="new-auction-images">Auction Images</label>
            <input id="new-auction-images" className="form-control" type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" onChange={addFiles} />
            <div className="form-text">Select multiple images. They upload when the draft is created.</div>
            <div className="d-flex flex-wrap gap-2 mt-2">{previews.map(({ file, url }, index) => <div className="border rounded p-2" key={`${file.name}-${index}`}>
              <img src={url} alt={`Selected auction lot ${index + 1}`} style={{ width: 110, height: 90, objectFit: "cover" }} />
              <button className="btn btn-sm btn-outline-danger d-block mt-1" type="button" aria-label={`Delete selected image ${index + 1}`} onClick={() => setFiles((previous) => previous.filter((_, candidate) => candidate !== index))}>Delete image</button>
            </div>)}</div>
          </div>
        </div>
        <div className="d-flex gap-2 mt-3"><button className="btn btn-primary" type="submit">{saving ? "Creating..." : "Create Auction Item"}</button><button className="btn btn-outline-secondary" type="button" onClick={onCancel}>Cancel</button></div>
      </fieldset>
      {uploadingIndex != null && <div className="mt-3">
        <div className="small">Uploading image {uploadingIndex + 1} of {files.length}</div>
        <div className="progress" role="progressbar" aria-label="New item image upload progress" aria-valuenow={totalProgress} aria-valuemin={0} aria-valuemax={100}><div className="progress-bar" style={{ width: `${totalProgress}%` }}>{totalProgress}%</div></div>
      </div>}
    </form>
  </section>;
}
